import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, CalendarIcon } from 'lucide-react';
import { useFamily } from '@/hooks/useFamily';
import { useOptimizedTasks } from '@/hooks/useOptimizedTasks';
import { useUniversalContacts } from '@/hooks/useUniversalContacts';
import { MultiSelect, MultiSelectOption } from '@/components/ui/multi-select';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useCurrentSection } from '@/hooks/useCurrentSection';
import { PageHeader } from '@/components/PageHeader';

export default function EditTask() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { family } = useFamily();
  const { tasks, updateTask, loading } = useOptimizedTasks(family?.id);
  const { contacts, getTaskAssignmentId } = useUniversalContacts(family?.id);
  const { primaryVariant, secondaryVariant } = useCurrentSection();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<Date>();
  const assigneesInitializedRef = useRef(false);

  const task = tasks?.find(t => t.id === id);

  // Load basic task data immediately when task is available
  useEffect(() => {
    if (task) {
      console.log('📝 Loading basic task data:', { title: task.title, description: task.description });
      setTitle(task.title);
      setDescription(task.description || '');
      if (task.due_date) {
        setDueDate(new Date(task.due_date));
      }
    }
  }, [task]);

  // Load assignees when task and contacts are ready (initialize once)
  useEffect(() => {
    if (!task || contacts.length === 0) return;
    if (assigneesInitializedRef.current) return; // Prevent immediate overwrite of user selection

    const assignees = task.assigned_to_list || [];
    console.log('📝 Loading task assignees (raw UUIDs):', assignees);

    // Map stored UUIDs back to prefixed contact IDs for the MultiSelect
    const prefixedIds = assignees
      .filter(Boolean)
      .map((storedUuid) => {
        const contact = contacts.find(c => getTaskAssignmentId(c) === storedUuid);
        if (contact) {
          console.log(`✅ Matched UUID ${storedUuid} to contact ${contact.id} (${contact.displayName})`);
          return contact.id; // Return prefixed ID (fm_xxx or pp_xxx)
        }
        console.warn(`⚠️ No contact found for UUID ${storedUuid}`);
        return null;
      })
      .filter(Boolean) as string[];

    console.log('🔄 Mapped to prefixed IDs for display:', prefixedIds);
    setAssignedTo(prefixedIds);
    assigneesInitializedRef.current = true;
  }, [task, contacts, getTaskAssignmentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Le titre ne peut pas être vide.",
      });
      return;
    }
    
    if (!id) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "ID de la tâche introuvable.",
      });
      return;
    }
    
    console.log('💾 Saving task with prefixed IDs:', assignedTo);
    
    // Convert prefixed contact IDs (fm_xxx, pp_xxx) back to actual UUIDs for storage
    const actualIds = assignedTo
      .map((prefixedId) => {
        const contact = contacts.find(c => c.id === prefixedId);
        if (!contact) {
          console.warn(`⚠️ Contact not found for prefixed ID: ${prefixedId}`);
          return null;
        }
        
        const taskId = getTaskAssignmentId(contact);
        console.log(`🔄 Converting ${prefixedId} (${contact.displayName}) to UUID: ${taskId}`);
        return taskId;
      })
      .filter(Boolean) as string[];
    
    console.log('💾 Final UUIDs to save:', actualIds);
    
    await updateTask(id, title, description, actualIds, dueDate);
    navigate('/tasks');
  };

  

  // Show loading state while data is being fetched
  if (!family) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeader title="Modifier la tâche" backPath="/tasks" />
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-muted-foreground text-center">
            Créez d'abord une famille pour pouvoir modifier des tâches.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state while task data is being fetched
  if (loading || (!task && tasks !== undefined)) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeader title="Modifier la tâche" backPath="/tasks" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">Chargement de la tâche...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if task not found after loading
  if (!task) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeader title="Modifier la tâche" backPath="/tasks" />
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-muted-foreground text-center">
            Tâche introuvable.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader title="Modifier la tâche" backPath="/tasks" />

      <div className="flex-1 p-4 pb-32">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="title" className="text-sm font-medium">
              Titre de la tâche *
            </label>
            <Input
              id="title"
              placeholder="Que faut-il faire ?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="description" className="text-sm font-medium">
              Description
            </label>
            <Textarea
              id="description"
              placeholder="Détails ou instructions (optionnel)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Attribuer à *
            </label>
            <MultiSelect
              options={contacts.map((contact): MultiSelectOption => ({
                value: contact.id,
                label: contact.displayName,
                avatar: contact.avatar_url,
                group: contact.source === 'family_member' ? 'Famille' : 
                      contact.originalData.category === 'family' ? 'Famille' :
                      contact.originalData.category === 'friends' ? 'Amis' : 'Autre',
              }))}
              selected={assignedTo}
              onChange={setAssignedTo}
              placeholder="Choisir une ou plusieurs personnes"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Date d'échéance
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, 'PPP', { locale: fr }) : "Choisir une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
            {dueDate && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDueDate(undefined)}
                className="text-muted-foreground"
              >
                Supprimer la date
              </Button>
            )}
          </div>
        </form>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 border-t bg-background space-y-3 z-50">
        <Button 
          onClick={handleSubmit}
          disabled={loading || !title.trim()}
          className="w-full"
          variant={primaryVariant as any}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
        <Button 
          variant="cancel-hover" 
          onClick={() => navigate('/tasks')}
          className="w-full"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}